import React, { useState } from 'react';
import axios from 'axios';

const StatementUpload = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unknownCards, setUnknownCards] = useState([]);
  const [statementMonth, setStatementMonth] = useState('');
  const [groupName, setGroupName] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    setSuccess('');
    setUnknownCards([]);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    if (!statementMonth) {
      setError('Please enter the statement month');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUnknownCards([]);

    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('statementMonth', statementMonth);
    if (groupName) {
      formData.append('groupName', groupName);
    }

    try {
      const response = await axios.post('/api/statements/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(`${response.data.message} - Statement Group: ${response.data.statementGroup.name}`);
      setUnknownCards(response.data.unknownCards || []);
      setFile(null);
      setStatementMonth('');
      setGroupName('');
      
      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Notify parent component
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to upload CSV file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          {success}
        </div>
      )}

      {unknownCards.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg mb-6">
          <p className="font-semibold">
            ⚠️ {unknownCards.reduce((sum, c) => sum + c.count, 0)} transaction(s) are on cards that don't exist in the system:
          </p>
          <ul className="mt-2 list-disc list-inside text-sm">
            {unknownCards.map((c) => (
              <li key={c.cardNumber}>
                Card <span className="font-mono font-semibold">{c.cardNumber}</span> — {c.count} transaction(s)
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm">
            These transactions were imported, but no user can see or match them until the card is
            added under Credit Cards and assigned to a user.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Statement Month *</label>
          <input
            type="month"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            value={statementMonth}
            onChange={(e) => setStatementMonth(e.target.value)}
            required
          />
          <p className="mt-2 text-xs text-gray-500">
            Select the month these transactions are for (e.g., 2024-01)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Group Name (Optional)</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Custom name for this statement group"
          />
          <p className="mt-2 text-xs text-gray-500">
            If not provided, a default name will be generated
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">CSV File *</label>
          <input
            id="csv-file-input"
            type="file"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            accept=".csv"
            onChange={handleFileChange}
            required
          />
          <p className="mt-2 text-xs text-gray-500">
            Upload a CSV file with columns: Card, Transaction Date, Post Date, Description, Category, Type, Amount, Memo
          </p>
        </div>

        {file && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Selected file:</span> {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          </div>
        )}

        <button 
          type="submit" 
          className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload CSV'}
        </button>
      </form>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-3">CSV Format Requirements:</h4>
        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
          <li>Only transactions with Type = "Sale" will be imported</li>
          <li>Required columns: Transaction Date, Amount, Type</li>
          <li>Date format: MM/DD/YY or MM/DD/YYYY</li>
          <li>Amount can include currency symbols (e.g., $, -)</li>
          <li>Maximum file size: 5MB</li>
        </ul>
      </div>
    </div>
  );
};

export default StatementUpload;